import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  InputAdornment,
  ListItemText,
  ListSubheader,
  MenuItem,
  TextField,
} from "@mui/material";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";

export const exactMulti = (row, colId, selected) => {
  if (!Array.isArray(selected) || selected.length === 0) return true;
  const v = row.getValue(colId);
  return selected.some((opt) => String(opt) === String(v));
};

function normalizeSelectOptions(fromDef) {
  if (!Array.isArray(fromDef) || !fromDef.length) return null;
  return fromDef
    .map((o) => {
      if (o == null) return "";
      if (typeof o === "object") return String(o.value ?? o.label ?? "");
      return String(o);
    })
    .filter(Boolean);
}

function getRowFilterValues(column, row) {
  const getTags = column?.columnDef?.meta?.getFilterTags;
  if (typeof getTags === "function") return getTags(row);
  const raw = row.getValue(column.id);
  return Array.isArray(raw) ? raw : [raw];
}

function uniqueFilterValues(column, rows) {
  const set = new Set();
  for (const row of rows) {
    for (const raw of getRowFilterValues(column, row)) {
      if (raw == null || raw === "") continue;
      set.add(String(raw));
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

function liveFacetedCounts(column) {
  const rows = column?.getFacetedRowModel?.()?.rows ?? [];
  const map = new Map();
  for (const row of rows) {
    for (const raw of getRowFilterValues(column, row)) {
      if (raw == null || raw === "") continue;
      const key = String(raw);
      map.set(key, (map.get(key) ?? 0) + 1);
    }
  }
  return map;
}

function rowValueSignature(column, rows) {
  return (rows ?? [])
    .map((row) => `${row.id}:${getRowFilterValues(column, row).join(",")}`)
    .join("|");
}

export default function FilterMultiSelectWithSearch({ column, table }) {
  const header = column?.columnDef?.header ?? column?.id ?? "Value";
  const staticOptions = normalizeSelectOptions(column?.columnDef?.filterSelectOptions);

  const facetedRows = column?.getFacetedRowModel?.()?.rows ?? [];
  const allRows =
    table?.getPreFilteredRowModel?.()?.rows ?? table?.getCoreRowModel?.()?.rows ?? facetedRows;
  const valueSignature = rowValueSignature(column, facetedRows);
  const allSignature = rowValueSignature(column, allRows);

  // Recalculate from current row values so counts update after data changes
  // (MRT's getFacetedUniqueValues can stay stale when row identities are stable).
  const faceted = useMemo(
    () => (staticOptions ? null : liveFacetedCounts(column)),
    [column, staticOptions, valueSignature]
  );
  const options = useMemo(() => {
    if (staticOptions) return staticOptions;
    return uniqueFilterValues(column, allRows);
  }, [allRows, allSignature, column, staticOptions]);

  // Selection must be an array
  const value = Array.isArray(column.getFilterValue()) ? column.getFilterValue() : [];

  // Search (menu-local)
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? options.filter((o) => o.toLowerCase().includes(needle)) : options;
  }, [options, q]);
  const enabledShown = useMemo(
    () => shown.filter((opt) => (faceted?.get?.(opt) ?? 0) > 0),
    [shown, faceted]
  );

  // Handlers
  const handleChange = (e) => {
    const next = e.target.value ?? [];
    const allowed = next.filter((opt) => value.includes(opt) || (faceted?.get?.(opt) ?? 0) > 0);
    column.setFilterValue(allowed);
  };

  const selectAllShown = (e) => {
    e.stopPropagation();
    column.setFilterValue(enabledShown);
  };

  const reset = (e) => {
    e.stopPropagation();
    column.setFilterValue([]);
    setQ("");
  };

  const filterLabel = `Filter by ${header}`;

  return (
    <TextField
      select
      variant="standard"
      size="small"
      fullWidth
      label={value.length ? filterLabel : undefined}
      slotProps={{
        inputLabel: value.length ? { shrink: true } : undefined,
        select: {
          multiple: true,
          displayEmpty: true,
          value,
          onChange: handleChange,
          renderValue: (sel) =>
            sel?.length ? (
              sel.join(", ")
            ) : (
              <Box component="span" sx={{ color: "text.secondary" }}>
                {filterLabel}
              </Box>
            ),
          MenuProps: {
            disableAutoFocusItem: true,
            PaperProps: { sx: { maxHeight: 420, minWidth: 320 } },
            MenuListProps: {
              dense: true,
              subheader: (
                <ListSubheader
                  disableSticky
                  sx={{ bgcolor: "background.paper", px: 1.5, pt: 1.25, pb: 1 }}
                >
                  {/* Search on top */}
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search menu options"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    autoComplete="off"
                    onKeyDown={(e) => e.stopPropagation()}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  {/* Buttons below search, opposing sides */}
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      onMouseDown={(e) => e.preventDefault()} // keep menu open
                      onClick={selectAllShown}
                    >
                      Select All
                    </Button>
                    <Button size="small" onMouseDown={(e) => e.preventDefault()} onClick={reset}>
                      Reset
                    </Button>
                  </Box>
                  <Divider sx={{ mt: 1 }} />
                </ListSubheader>
              ),
            },
          },
        },
      }}
    >
      {shown.map((opt) => {
        const checked = value.includes(opt);
        const count = staticOptions ? null : (faceted?.get?.(opt) ?? 0);
        const disabled = count === 0 && !checked;
        return (
          <MenuItem
            key={opt}
            value={opt}
            disabled={disabled}
            sx={{ py: 0.5, px: 1.25, "& .MuiCheckbox-root": { mr: 1 } }}
          >
            <Checkbox size="small" checked={checked} disabled={disabled} />
            <ListItemText
              slotProps={{
                primary: { variant: "body2" },
              }}
              primary={`${opt}${count == null ? "" : ` (${count})`}`}
            />
          </MenuItem>
        );
      })}
      {!shown.length && <MenuItem disabled={true}>No options</MenuItem>}
    </TextField>
  );
}

FilterMultiSelectWithSearch.propTypes = {
  column: PropTypes.shape({
    id: PropTypes.string,
    columnDef: PropTypes.shape({
      header: PropTypes.string,
      filterSelectOptions: PropTypes.array,
      meta: PropTypes.shape({
        getFilterTags: PropTypes.func,
      }),
    }),
    getFilterValue: PropTypes.func,
    setFilterValue: PropTypes.func,
    getFacetedRowModel: PropTypes.func,
    getFacetedUniqueValues: PropTypes.func,
  }).isRequired,
  table: PropTypes.shape({
    getPreFilteredRowModel: PropTypes.func,
    getCoreRowModel: PropTypes.func,
  }),
};
